import { FC, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  HStack,
  Heading,
  Image,
  LinkBox,
  LinkOverlay,
  SimpleGrid,
  VStack,
  Text,
  Button,
  MenuItem,
  Menu,
  MenuButton,
  MenuList,
  Switch,
  useToast,
} from '@chakra-ui/react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { createNewCompanyContent } from '../utils/new-company-content';
import {
  useGetCompanyByIdQuery,
  useGetCompanyLogoUrlQuery,
  useToggleCompanyStatusMutation,
  useDeleteCompanyMutation,
} from '../../../services';
import { CenteredSpinner, RoundedBadge } from '../components';
import { ReactComponent as ChevronUpIcon } from '../../../assets/icons/chevron-up.svg';
import RedTrashIcon from '../../../assets/icons/red-trash.svg';
import LinkExternal from '../../../assets/icons/link-external.svg';
import { Status } from '../../../models';
import { getMutationFailureMessage } from '../utils';
// The canonical envelope classifier. `isSuccess` is deliberately NOT imported
// here — this component already destructures an `isSuccess` flag off the
// company query and the two must not be confused.
import {
  MutationResult,
  classifyMutationResult,
} from '../../../utils/api-response';

// core-api's `company/changeStatus` status ids. Named because the bare 1/2 in
// a ternary is exactly what let the payload drift out of step with the intent.
const ACTIVE_STATUS_ID = 1;
const INACTIVE_STATUS_ID = 2;

export const SavedCompany: FC = () => {
  const params = useParams();
  const { companyId } = params;
  const status = params.status || 'all';

  const navigate = useNavigate();

  const {
    data,
    isLoading: isGetCompanyByIdLoading,
    isSuccess,
    refetch,
  } = useGetCompanyByIdQuery(Number(companyId), {
    skip: !companyId,
  });

  const { data: companyLogo } = useGetCompanyLogoUrlQuery(Number(companyId), {
    skip: !companyId,
  });

  const [isCompanyActive, setIsCompanyActive] = useState(false);
  const [toggleCompanyStatus, { isLoading }] = useToggleCompanyStatusMutation();
  const [deleteCompany] = useDeleteCompanyMutation();

  const toast = useToast();

  // The server's status is the source of truth for the switch, so this syncs on
  // the STATUS VALUE and not only on the first `isSuccess`. Without the value in
  // the deps a post-toggle refetch would land new data and leave the switch
  // showing the optimistic guess forever. Both deps are primitives, so a query
  // hook returning a fresh object per render cannot loop this.
  useEffect(() => {
    setIsCompanyActive(data?.data.status === Status.Active);
  }, [isSuccess, data?.data.status]);

  // This repo has no global error notifier, so an unreported failure here is
  // invisible. Same local-toast pattern the other companies pages use.
  const showFailure = (title: string, response: unknown) =>
    toast({
      title,
      description: getMutationFailureMessage(response),
      status: 'error',
      duration: 9000,
      isClosable: true,
    });

  const handleToggleCompanyStatus = async () => {
    // Capture the pre-request truth ONCE, in a local. The request payload and
    // the rollback both read this constant rather than `isCompanyActive`, so
    // neither depends on whether React has applied the optimistic update below
    // — previously the payload was only correct because it happened to run
    // before that update landed.
    const wasActive = isCompanyActive;
    const nextActive = !wasActive;

    // Optimistic in the direction actually requested. Was `true` unconditionally,
    // so a deactivation rendered as active.
    setIsCompanyActive(nextActive);

    // Not `.unwrap()`: it rejected on transport failures with nothing to catch
    // them (this runs from a `void` call), and it resolved on 200-wrapped body
    // failures. The classifier covers both bands.
    const response = await toggleCompanyStatus({
      companyId: Number(companyId),
      statusId: nextActive ? ACTIVE_STATUS_ID : INACTIVE_STATUS_ID,
    });

    // Was `data.httpStatus !== 'OK'`, the decorative dialect. errorCode is
    // canonical for body-level outcomes — see src/utils/api-response.ts.
    if (classifyMutationResult(response) !== MutationResult.SUCCESS) {
      // Restore what was on screen before the request. Was `false`
      // unconditionally, so a failed deactivation of an active company rendered
      // the opposite of the truth.
      setIsCompanyActive(wasActive);
      showFailure('Could not change the company status', response);
      return;
    }

    // `toggleCompanyStatus` declares no `invalidatesTags`, so nothing re-reads
    // the company on its own and the cached envelope keeps the old status. Pull
    // it again and let the effect above settle the switch on the server's
    // answer; the optimistic update is only the gap-filler until it arrives.
    void refetch();
  };

  const handleDeleteCompany = async () => {
    // Previously: awaited unchecked and navigated away regardless, so a refused
    // delete looked exactly like a successful one. Navigate ONLY on a confirmed
    // body-level success; stay put and say so otherwise.
    const response = await deleteCompany({ companyId: Number(companyId) });

    if (classifyMutationResult(response) !== MutationResult.SUCCESS) {
      showFailure('Could not delete company', response);
      return;
    }

    navigate(`/companies/${status}`);
  };

  if (isGetCompanyByIdLoading) return <CenteredSpinner />;

  return (
    <Box h="full">
      {companyId ? (
        <HStack justify="space-between" mt="4" mb="12" align="start">
          <HStack align="center" spacing="4">
            {companyLogo?.data && (
              <Image
                src={companyLogo.data}
                alt={`${data?.data.name} logo`}
                boxSize="14"
                borderRadius="md"
                objectFit="cover"
                bg="custom_gray.4"
              />
            )}
            <VStack align="start">
            <HStack>
              <Heading color="custom_blue.1" fontSize="2xl">
                {data?.data.name}
              </Heading>

              {data?.data.status && (
                <RoundedBadge colorSchema={data?.data.status}>
                  {data?.data.status}
                </RoundedBadge>
              )}

              {data?.data.subscriptionPlan && (
                <RoundedBadge colorSchema={data?.data.subscriptionPlan}>
                  {data?.data.subscriptionPlan}
                </RoundedBadge>
              )}
            </HStack>
            <Text fontSize="md" color="custom_gray.1">
              {data?.data.companyType} Company
            </Text>
            </VStack>
          </HStack>

          <HStack>
            <Menu closeOnSelect={false}>
              {() => (
                <>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronUpIcon />}
                    fontSize="md"
                    bg="none"
                    _hover={{ bg: 'none' }}
                    border="1px"
                    borderColor="custom_gray.1"
                  >
                    Actions
                  </MenuButton>
                  <MenuList p="0">
                    <MenuItem border="1px" borderColor="custom_gray.4" p="3">
                      <HStack justify="space-between" w="full">
                        <Text fontSize="sm" fontWeight="medium">
                          {/*
                            A VERB naming the action the click performs, not a
                            label describing the current state: an ACTIVE company
                            is offered 'Deactivate'. Was the other way round, so
                            the menu promised the opposite of what it did. The
                            wording is independent of the `statusId` sent by
                            `handleToggleCompanyStatus` — do not "make them
                            agree" by flipping the payload too, which would
                            invert the actual behaviour.
                          */}
                          {isCompanyActive ? 'Deactivate' : 'Activate'}
                        </Text>

                        <Switch
                          isChecked={isCompanyActive}
                          isDisabled={isLoading}
                          colorScheme="green"
                          onChange={() => {
                            void handleToggleCompanyStatus();
                          }}
                        />
                      </HStack>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        void handleDeleteCompany();
                      }}
                      border="1px"
                      borderColor="custom_gray.4"
                      p="3"
                    >
                      <HStack>
                        <Image
                          src={RedTrashIcon}
                          alt="Delete company"
                          color="red"
                        />
                        <Text
                          color="error.600"
                          fontSize="sm"
                          fontWeight="medium"
                        >
                          Delete
                        </Text>
                      </HStack>
                    </MenuItem>
                  </MenuList>
                </>
              )}
            </Menu>
            <Button
              bg="custom_blue.1"
              borderRadius="md"
              _hover={{ bg: 'custom_blue.1' }}
            >
              <HStack>
                <Image src={LinkExternal} alt="Link External" />
                <Text color="custom_gray.2" fontSize="sm" fontWeight="semibold">
                  Live Preview Access
                </Text>
              </HStack>
            </Button>
          </HStack>
        </HStack>
      ) : (
        <HStack justify="space-between" mt="4" mb="12" align="start">
          <Heading color="custom_blue.1" fontSize="2xl">
            Add New Company
          </Heading>
        </HStack>
      )}

      <SimpleGrid columns={3} spacing={8}>
        {createNewCompanyContent(
          data?.data.status,
          // Backend Company.numberOfProjects is typed string; consumer expects number.
          // Number(undefined) = NaN; consumer's `<= 0` check is false for both NaN and
          // the prior implicit-undefined path, so behavior is preserved.
          Number(data?.data.numberOfProjects)
        ).map(({ image, link, title, isDisabled, isHidden }) => {
          if (isHidden) return null;

          return (
            <LinkBox key={title}>
              <Card
                boxShadow="0 12px 16px -4px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)"
                opacity={
                  (!companyId && link !== 'basic-info') || isDisabled
                    ? '.5'
                    : '1'
                }
              >
                <CardBody
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  color="custom_blue.1"
                >
                  <Image src={`/companies/${image}.svg`} mb="4" />

                  <Box fontWeight="bold" fontSize="2xl" mb="4">
                    {companyId || link === 'basic-info' || isDisabled ? (
                      <LinkOverlay as={Link} to={`${link}`}>
                        {title}
                      </LinkOverlay>
                    ) : (
                      <LinkOverlay>{title}</LinkOverlay>
                    )}
                  </Box>

                  <Box opacity=".7" fontSize="sm">
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                    Pelle
                  </Box>
                </CardBody>
              </Card>
            </LinkBox>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};
